import { useCallback, useEffect, useRef, useState } from 'react';
import type { ChangeEvent } from 'react';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  providerMeApi,
  notificationApi,
  calendarApi,
} from '../../api/providerMeApi';
import type {
  NotificationPreferences,
  CalendarStatus,
} from '../../api/providerMeApi';
import { telegram } from '../../utils/telegram';
import { telegramLinks } from '../../utils/telegramLinks';
import {
  validateProviderPhoto,
  ALLOWED_IMAGE_TYPES,
} from '../../utils/imageValidation';
import type {
  ProviderProfile,
  ProviderService,
  ServiceType,
} from '../../types/provider';
import type { LanguageCode } from '../../types/i18n';
import { useLanguageStore } from '../../store/languageStore';
import { useAuthStore } from '../../store/authStore';
import { usePullToRefresh } from '../../hooks/usePullToRefresh';
import { useRefreshOnVisibility } from '../../hooks/useRefreshOnVisibility';
import PullToRefreshIndicator from '../shared/PullToRefreshIndicator';
import ProfileTabSkeleton from './skeletons/ProfileTabSkeleton';
import { LanguageSwitcherCard } from './LanguageSwitcherCard';
import { ProfileHero } from './profile/ProfileHero';
import { ServicesSection } from './profile/ServicesSection';
import { NotificationsSection } from './profile/NotificationsSection';
import { CalendarSection } from './profile/CalendarSection';
import { PhotoActionSheet } from './profile/PhotoActionSheet';
import { EditProfileModal } from './profile/EditProfileModal';
import {
  ServiceModal,
  type ServiceModalMode,
} from './profile/ServiceModal';
import { SectionShell } from './profile/SectionShell';
import ConfirmSheet from './shared/ConfirmSheet';
import { GlobalOutlined } from '@ant-design/icons';

// Calendar endpoint'lari uchun xatoliklarni i18n kalitiga moslaydi.
// Spec §7 (Error handling matritsasi) — har bir HTTP/code juftligi uchun aniq
// xabar. defaultKey — backend code'sini aniqlab bo'lmaganda fallback.
type CalendarErrorKey =
  | 'profile.calendar.premiumOnly'
  | 'profile.calendar.providerNotFound'
  | 'profile.calendar.temporaryError'
  | 'profile.calendar.connectFailed'
  | 'profile.calendar.checkFailed'
  | 'profile.calendar.disconnectFailed';

interface CalendarErrorInfo {
  key: CalendarErrorKey;
  severity: 'warning' | 'error';
}

function mapCalendarError(
  err: unknown,
  defaultKey: CalendarErrorKey,
): CalendarErrorInfo {
  const error = err as {
    response?: { status?: number; data?: { code?: string } };
  };
  const status = error?.response?.status;
  const code = error?.response?.data?.code;
  if (status === 403 && code === 'FEATURE_NOT_AVAILABLE') {
    return { key: 'profile.calendar.premiumOnly', severity: 'warning' };
  }
  if (status === 404) {
    return { key: 'profile.calendar.providerNotFound', severity: 'error' };
  }
  if (status === 502 || status === 503 || status === 504) {
    return { key: 'profile.calendar.temporaryError', severity: 'error' };
  }
  return { key: defaultKey, severity: 'error' };
}

export default function ProfileTab() {
  const { t, i18n } = useTranslation(['common', 'provider']);

  const { currentLanguage, supportedLanguages, defaultLanguage } =
    useLanguageStore();

  // Calendar endpointlari path'ida tenantId talab qiladi — sessiyadan olamiz.
  const tenantId = useAuthStore(
    (s) => s.session?.tenantContext.tenantId ?? null,
  );

  // ─── Sahifa darajasidagi state ────────────────────────────────────────────
  const [profile, setProfile] = useState<ProviderProfile | null>(null);
  const [services, setServices] = useState<ProviderService[]>([]);
  const [serviceTypes, setServiceTypes] = useState<ServiceType[]>([]);
  const [notifPrefs, setNotifPrefs] = useState<NotificationPreferences | null>(
    null,
  );
  const [loading, setLoading] = useState(true);

  // ─── Profilni tahrirlash ──────────────────────────────────────────────────
  const [editingProfile, setEditingProfile] = useState(false);
  const [editNames, setEditNames] = useState<
    Partial<Record<LanguageCode, string>>
  >({});
  const [editBios, setEditBios] = useState<
    Partial<Record<LanguageCode, string>>
  >({});
  const [savingProfile, setSavingProfile] = useState(false);

  // ─── Profil rasmi ─────────────────────────────────────────────────────────
  const [photoSheetOpen, setPhotoSheetOpen] = useState(false);
  const [photoDeleteConfirmOpen, setPhotoDeleteConfirmOpen] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [deletingPhoto, setDeletingPhoto] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ─── Xizmat modal'i ───────────────────────────────────────────────────────
  const [editService, setEditService] =
    useState<Partial<ProviderService> | null>(null);
  const [editMode, setEditMode] = useState<ServiceModalMode>('create');
  const [savingService, setSavingService] = useState(false);

  // ─── Google Calendar ──────────────────────────────────────────────────────
  // Status endpoint javobi — connected + kill switch / PREMIUM availability.
  // Initial null: status hali yuklanmagan. loadData' uni yuklaydi va render
  // shu state'ga tayanadi (eski profile.googleCalendarConnected emas).
  const [calendarStatus, setCalendarStatus] = useState<CalendarStatus | null>(
    null,
  );
  const [calendarConnecting, setCalendarConnecting] = useState(false);
  const [calendarChecking, setCalendarChecking] = useState(false);
  const [calendarDisconnecting, setCalendarDisconnecting] = useState(false);
  // OAuth oqimi davom etayotganini belgilaydi — foydalanuvchi Google ruxsat
  // sahifasiga yo'naltirilgan. WebApp yana faollashganda holat avtomatik
  // tekshiriladi.
  const oauthPendingRef = useRef<{
    tenantId: number;
    providerId: number;
  } | null>(null);
  // OAuth qaytishidan keyingi holat-tekshirish polling'i uchun.
  const pollTimerRef = useRef<number | undefined>(undefined);
  const pollingActiveRef = useRef(false);
  const mountedRef = useRef(true);

  const slotStep = profile?.tenantSlotStepMinutes ?? 15;

  // ─── Data loading ─────────────────────────────────────────────────────────
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [p, s, types, np] = await Promise.all([
        providerMeApi.getProfile(),
        providerMeApi.getServices(),
        providerMeApi.getServiceTypes().catch(() => []),
        notificationApi.getPreferences().catch(() => null),
      ]);
      setProfile(p);
      setServices(s);
      setServiceTypes(types);
      setNotifPrefs(np);
      // Calendar status — kill switch (FEATURE_DISABLED) va PREMIUM holatini
      // bildiradi. Faqat backend qaytargan qiymat ishlatiladi — hech qanday
      // fallback / taxmin yo'q. Status hali yuklanmagan yoki xato bersa
      // `calendarStatus` `null` qoladi va CalendarSection umuman
      // renderlanmaydi (pastda conditional render'ga qarang).
      //
      // Sequential — yuqoridagi Promise.all bilan birga emas, chunki status
      // endpointi path'ida `providerId = p.id` talab qiladi va u faqat
      // profile yuklangach ma'lum bo'ladi. Round-trip'ni bartaraf etish
      // uchun backend'ga `available`/`reason` ni `/v1/provider/me` javobiga
      // qo'shish so'rovi yuborilishi mumkin.
      if (tenantId != null) {
        try {
          const status = await calendarApi.getStatus(tenantId, p.id);
          if (mountedRef.current) setCalendarStatus(status);
        } catch {
          // Xato bo'lsa eski qiymatni (agar mavjud bo'lsa) saqlaymiz —
          // keyingi refresh (pull-to-refresh, useRefreshOnVisibility)
          // status'ni yangilaydi. Taxmin qilmaymiz.
        }
      }
    } catch {
      void message.error(t('provider:profile.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t, tenantId]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  useEffect(() => {
    const handler = () => {
      void loadData();
    };
    i18n.on('languageChanged', handler);
    return () => {
      i18n.off('languageChanged', handler);
    };
  }, [i18n, loadData]);

  const { containerRef, pullDistance, isRefreshing, progress } =
    usePullToRefresh<HTMLDivElement>({
      onRefresh: loadData,
      disabled: loading,
    });

  // ─── Profil tahrirlash flow ───────────────────────────────────────────────
  const handleOpenEditProfile = () => {
    if (!profile) return;
    const namesMap: Partial<Record<LanguageCode, string>> = {};
    const biosMap: Partial<Record<LanguageCode, string>> = {};

    if (profile.translations && profile.translations.length > 0) {
      profile.translations.forEach((tr) => {
        namesMap[tr.languageCode] = tr.displayName;
        biosMap[tr.languageCode] = tr.bio ?? '';
      });
    } else {
      supportedLanguages.forEach((lang) => {
        namesMap[lang as LanguageCode] = profile.displayName;
        biosMap[lang as LanguageCode] = profile.bio ?? '';
      });
    }
    setEditNames(namesMap);
    setEditBios(biosMap);
    setEditingProfile(true);
  };

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      const translationsArr =
        supportedLanguages.length > 1
          ? supportedLanguages
              .filter((lang) => editNames[lang as LanguageCode]?.trim())
              .map((lang) => ({
                languageCode: lang as LanguageCode,
                displayName: editNames[lang as LanguageCode]!.trim(),
                bio: editBios[lang as LanguageCode]?.trim() || null,
              }))
          : undefined;

      const primaryName = (
        editNames[defaultLanguage as LanguageCode] ??
        editNames['uz'] ??
        Object.values(editNames)[0] ??
        ''
      ).trim();
      const primaryBio =
        (
          editBios[defaultLanguage as LanguageCode] ??
          editBios['uz'] ??
          Object.values(editBios)[0] ??
          ''
        ).trim() || null;

      const updated = await providerMeApi.updateProfile({
        displayName: primaryName,
        bio: primaryBio,
        translations: translationsArr,
      });
      setProfile(updated);
      setEditingProfile(false);
      telegram.hapticFeedback.notification('success');
    } catch (err: unknown) {
      const error = err as {
        response?: { data?: { message?: string } };
      };
      void message.error(
        error?.response?.data?.message ??
          t('provider:profile.saveFailed'),
      );
    } finally {
      setSavingProfile(false);
    }
  };

  // ─── Profil rasmi flow ────────────────────────────────────────────────────
  const handleOpenPhotoSheet = () => {
    if (uploadingPhoto || deletingPhoto) return;
    telegram.hapticFeedback.impact('light');
    setPhotoSheetOpen(true);
  };

  const triggerFilePicker = () => {
    setPhotoSheetOpen(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  const handleFileSelected = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const validation = validateProviderPhoto(file);
    if (!validation.ok) {
      telegram.hapticFeedback.notification('error');
      void message.error(validation.reason);
      e.target.value = '';
      return;
    }
    setUploadingPhoto(true);
    try {
      const updated = await providerMeApi.uploadPhoto(file);
      setProfile(updated);
      telegram.hapticFeedback.notification('success');
      void message.success(t('provider:profile.photo.updated'));
    } catch (err: unknown) {
      const error = err as {
        response?: { data?: { message?: string; code?: string } };
      };
      const code = error?.response?.data?.code;
      const fallback =
        code === 'FILE_TOO_LARGE'
          ? t('provider:profile.photo.fileTooLarge')
          : code === 'FILE_INVALID_TYPE'
            ? t('provider:profile.photo.fileInvalidType')
            : t('provider:profile.photo.uploadFailed');
      telegram.hapticFeedback.notification('error');
      void message.error(error?.response?.data?.message ?? fallback);
    } finally {
      setUploadingPhoto(false);
      e.target.value = '';
    }
  };

  const handleDeletePhoto = () => {
    setPhotoSheetOpen(false);
    setPhotoDeleteConfirmOpen(true);
  };

  const handleConfirmDeletePhoto = async () => {
    setDeletingPhoto(true);
    try {
      const updated = await providerMeApi.deletePhoto();
      setProfile(updated);
      telegram.hapticFeedback.notification('success');
      void message.success(t('provider:profile.photo.deleted'));
      setPhotoDeleteConfirmOpen(false);
    } catch (err: unknown) {
      const error = err as {
        response?: { data?: { message?: string } };
      };
      telegram.hapticFeedback.notification('error');
      void message.error(
        error?.response?.data?.message ??
          t('provider:profile.photo.deleteFailed'),
      );
    } finally {
      setDeletingPhoto(false);
    }
  };

  // ─── Bildirishnomalar ─────────────────────────────────────────────────────
  const handleToggleNotif = useCallback(
    async (field: keyof NotificationPreferences) => {
      if (!notifPrefs) return;
      const previous = notifPrefs;
      const optimistic = { ...notifPrefs, [field]: !notifPrefs[field] };
      setNotifPrefs(optimistic);
      try {
        const result = await notificationApi.updatePreferences({
          [field]: !notifPrefs[field],
        });
        setNotifPrefs(result);
        telegram.hapticFeedback.impact('light');
      } catch {
        setNotifPrefs(previous);
        void message.error(t('provider:profile.notifications.toggleFailed'));
      }
    },
    [notifPrefs, t],
  );

  // ─── Xizmatlar ────────────────────────────────────────────────────────────
  const handleOpenAddService = () => {
    setEditService({
      serviceTypeId: undefined,
      durationMinutes:
        profile?.tenantDefaultServiceDurationMinutes ?? slotStep,
      price: undefined,
      bookingMode: 'FREE_AUTO_CONFIRM',
    });
    setEditMode('create');
  };

  const handleOpenEditService = (service: ProviderService) => {
    setEditService(service);
    setEditMode('edit');
  };

  const handleToggleService = async (service: ProviderService) => {
    // Optimistic update — Switch darhol yangi holatda ko'rsatiladi
    const nextActive = !service.active;
    setServices((prev) =>
      prev.map((s) => (s.id === service.id ? { ...s, active: nextActive } : s)),
    );
    telegram.hapticFeedback.impact('light');

    try {
      const updated = service.active
        ? await providerMeApi.deactivateService(service.id)
        : await providerMeApi.activateService(service.id);
      // Server javobi bilan sinxronlash (boshqa maydonlar ham yangilangan bo'lishi mumkin)
      setServices((prev) =>
        prev.map((s) => (s.id === service.id ? updated : s)),
      );
    } catch {
      // Xatolik — original holatga qaytarish
      setServices((prev) =>
        prev.map((s) => (s.id === service.id ? service : s)),
      );
      void message.error(t('provider:profile.services.toggleFailed'));
    }
  };

  const handleSaveService = async (payload: Partial<ProviderService>) => {
    setSavingService(true);
    try {
      if (editMode === 'create') {
        const created = await providerMeApi.createService({
          serviceTypeId: payload.serviceTypeId!,
          durationMinutes: payload.durationMinutes!,
          price: payload.price!,
          bookingMode: payload.bookingMode,
        });
        setServices((prev) => [...prev, created]);
      } else if (payload.id) {
        const updated = await providerMeApi.updateService(payload.id, {
          durationMinutes: payload.durationMinutes!,
          price: payload.price!,
          bookingMode: payload.bookingMode,
        });
        setServices((prev) =>
          prev.map((s) => (s.id === updated.id ? updated : s)),
        );
      }
      setEditService(null);
      telegram.hapticFeedback.notification('success');
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } } };
      void message.error(
        error?.response?.data?.message ??
          t('provider:profile.services.saveFailed'),
      );
    } finally {
      setSavingService(false);
    }
  };

  const usedServiceTypeIds = new Set(
    services.map((s) => s.serviceTypeId).filter((id) => id !== null),
  );

  // ─── Google Calendar ──────────────────────────────────────────────────────
  // Calendar API xatoliklarini foydalanuvchiga ko'rsatish (spec §7 matritsasi).
  const showCalendarError = useCallback(
    (err: unknown, defaultKey: CalendarErrorKey) => {
      const { key, severity } = mapCalendarError(err, defaultKey);
      const msg = t(`provider:${key}`);
      if (severity === 'warning') void message.warning(msg);
      else void message.error(msg);
    },
    [t],
  );

  const handleConnectCalendar = async () => {
    if (!profile || tenantId == null) {
      void message.error(t('provider:profile.calendar.connectFailed'));
      return;
    }
    setCalendarConnecting(true);
    try {
      const authUrl = await calendarApi.getAuthUrl(tenantId, profile.id);
      // OAuth qaytganda holatni avtomatik tekshirish uchun belgilab qo'yamiz.
      oauthPendingRef.current = { tenantId, providerId: profile.id };
      // Telegram WebApp ichida tashqi havolani ochish. Eslatma: spec §6.1 da
      // `try_instant_view: false` tavsiya qilingan, lekin @twa-dev/sdk uni
      // faqat truthy qiymatda backendga uzatadi — `false` no-op. Google
      // `disallowed_useragent` muammosi bartaraf etilsa, Bot API 7.6+ ning
      // `try_browser` parametrini cast bilan qo'shish kerak bo'ladi.
      telegramLinks.openExternalUrl(authUrl);
    } catch (err: unknown) {
      oauthPendingRef.current = null;
      showCalendarError(err, 'profile.calendar.connectFailed');
    } finally {
      setCalendarConnecting(false);
    }
  };

  const handleCheckCalendarStatus = async () => {
    if (!profile || tenantId == null) {
      void message.error(t('provider:profile.calendar.checkFailed'));
      return;
    }
    setCalendarChecking(true);
    try {
      const status = await calendarApi.getStatus(tenantId, profile.id);
      setCalendarStatus(status);
      if (status.connected) {
        oauthPendingRef.current = null;
        telegram.hapticFeedback.notification('success');
        void message.success(t('provider:profile.calendar.connectedMsg'));
      }
    } catch (err: unknown) {
      showCalendarError(err, 'profile.calendar.checkFailed');
    } finally {
      setCalendarChecking(false);
    }
  };

  const handleDisconnectCalendar = async () => {
    if (!profile || tenantId == null) {
      void message.error(t('provider:profile.calendar.disconnectFailed'));
      return;
    }
    setCalendarDisconnecting(true);
    try {
      await calendarApi.disconnect(tenantId, profile.id);
      oauthPendingRef.current = null;
      setCalendarStatus((prev) =>
        prev ? { ...prev, connected: false } : prev,
      );
      telegram.hapticFeedback.notification('success');
      void message.info(t('provider:profile.calendar.disconnectedMsg'));
    } catch (err: unknown) {
      showCalendarError(err, 'profile.calendar.disconnectFailed');
    } finally {
      setCalendarDisconnecting(false);
    }
  };

  // Unmount'da kutilayotgan polling timerini tozalash va keyingi async
  // javoblar state'ga yozmasligi uchun mounted bayrog'ini tushirish.
  //
  // MUHIM: setup'da ham `true` qilamiz — React 18 StrictMode development'da
  // effekt mount → cleanup → mount qayta tartibida ishga tushadi. Cleanup'da
  // `false` qilingan mountedRef re-setup'da `true` ga qaytarilmasa, keyingi
  // async setCalendarStatus chaqiruvlari `if (mountedRef.current)` orqasida
  // jim qoldiriladi va calendarStatus null qolib ketadi.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (pollTimerRef.current !== undefined) {
        window.clearTimeout(pollTimerRef.current);
      }
    };
  }, []);

  // OAuth qaytishidan keyin ulanish holatini bir necha urinish bilan
  // tekshiradi. Backend callback'ni qayta ishlashga vaqt kerak bo'lishi
  // mumkin — shuning uchun 2.5 soniya oralig'ida 3 martagacha so'raladi.
  const pollCalendarStatus = useCallback(
    async (attempt: number): Promise<void> => {
      const pending = oauthPendingRef.current;
      if (!pending) {
        pollingActiveRef.current = false;
        if (mountedRef.current) setCalendarChecking(false);
        return;
      }
      try {
        const status = await calendarApi.getStatus(
          pending.tenantId,
          pending.providerId,
        );
        if (!mountedRef.current) return;
        if (status.connected) {
          oauthPendingRef.current = null;
          pollingActiveRef.current = false;
          setCalendarStatus(status);
          telegram.hapticFeedback.notification('success');
          void message.success(t('provider:profile.calendar.connectedMsg'));
          setCalendarChecking(false);
        } else if (attempt < 2) {
          pollTimerRef.current = window.setTimeout(() => {
            void pollCalendarStatus(attempt + 1);
          }, 2500);
        } else {
          // Backend hali ulanishni qayd etmadi — "Holatni tekshirish"
          // tugmasi qo'lda fallback bo'lib qoladi.
          oauthPendingRef.current = null;
          pollingActiveRef.current = false;
          setCalendarChecking(false);
        }
      } catch {
        oauthPendingRef.current = null;
        pollingActiveRef.current = false;
        if (mountedRef.current) setCalendarChecking(false);
      }
    },
    [t],
  );

  // Foydalanuvchi Google ruxsat sahifasidan WebApp'ga qaytganda chaqiriladi
  // (visibilitychange + Telegram 'activated' eventi). OAuth kutilayotgan
  // bo'lsa va polling hali boshlanmagan bo'lsa — tekshiruvni boshlaydi.
  const handleOAuthReturn = useCallback(() => {
    if (!oauthPendingRef.current || pollingActiveRef.current) return;
    pollingActiveRef.current = true;
    setCalendarChecking(true);
    pollTimerRef.current = window.setTimeout(() => {
      void pollCalendarStatus(0);
    }, 1200);
  }, [pollCalendarStatus]);

  useRefreshOnVisibility(handleOAuthReturn);

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div
      ref={containerRef}
      style={{ height: '100%', overflowY: 'auto', position: 'relative' }}
    >
      <PullToRefreshIndicator
        pullDistance={pullDistance}
        isRefreshing={isRefreshing}
        progress={progress}
      />
      <div
        style={{
          padding: 16,
          paddingBottom: 'calc(32px + env(safe-area-inset-bottom, 0px))',
        }}
      >
        {loading ? (
          <ProfileTabSkeleton />
        ) : (
          <div key="ready" className="bl-animate-in">
            {profile && (
              <ProfileHero
                profile={profile}
                uploading={uploadingPhoto}
                deleting={deletingPhoto}
                onPhotoZoneActivate={handleOpenPhotoSheet}
                onEditProfile={handleOpenEditProfile}
              />
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept={ALLOWED_IMAGE_TYPES.join(',')}
              onChange={handleFileSelected}
              style={{ display: 'none' }}
            />

            <ServicesSection
              services={services}
              serviceTypes={serviceTypes}
              currentLanguage={currentLanguage as LanguageCode}
              defaultLanguage={defaultLanguage as LanguageCode}
              onAddService={handleOpenAddService}
              onEditService={handleOpenEditService}
              onToggleService={handleToggleService}
            />

            <NotificationsSection
              prefs={notifPrefs}
              onToggle={handleToggleNotif}
            />

            {calendarStatus && (
              <CalendarSection
                status={calendarStatus}
                connecting={calendarConnecting}
                disconnecting={calendarDisconnecting}
                checking={calendarChecking}
                onConnect={handleConnectCalendar}
                onDisconnect={handleDisconnectCalendar}
                onCheckStatus={handleCheckCalendarStatus}
              />
            )}

            {/* Til o'tkazgich — kam ishlatiladigan sozlama, pastga ko'chirildi */}
            {supportedLanguages.length > 1 && (
              <SectionShell
                caption={t('provider:profile.sections.languageCaption')}
                icon={<GlobalOutlined />}
                iconColor="#9254de"
              >
                <LanguageSwitcherCard
                  supportedLanguages={supportedLanguages as LanguageCode[]}
                />
              </SectionShell>
            )}
          </div>
        )}
      </div>

      {/* ─── Modals & Sheets ────────────────────────────────────────────── */}
      <EditProfileModal
        open={editingProfile}
        profile={profile}
        saving={savingProfile}
        editNames={editNames}
        editBios={editBios}
        setEditNames={setEditNames}
        setEditBios={setEditBios}
        supportedLanguages={supportedLanguages as LanguageCode[]}
        defaultLanguage={defaultLanguage as LanguageCode}
        onCancel={() => setEditingProfile(false)}
        onSave={handleSaveProfile}
      />

      <ServiceModal
        open={!!editService}
        mode={editMode}
        editService={editService}
        saving={savingService}
        serviceTypes={serviceTypes}
        usedServiceTypeIds={usedServiceTypeIds}
        slotStep={slotStep}
        currentLanguage={currentLanguage as LanguageCode}
        defaultLanguage={defaultLanguage as LanguageCode}
        onChange={(next) => setEditService(next)}
        onCancel={() => setEditService(null)}
        onSave={handleSaveService}
      />

      <PhotoActionSheet
        open={photoSheetOpen}
        hasPhoto={!!profile?.photoUrl}
        busy={uploadingPhoto || deletingPhoto}
        onClose={() => setPhotoSheetOpen(false)}
        onUpload={triggerFilePicker}
        onDelete={handleDeletePhoto}
      />

      <ConfirmSheet
        open={photoDeleteConfirmOpen}
        title={t('provider:profile.photo.deleteConfirmTitle')}
        description={t('provider:profile.photo.deleteConfirmContent')}
        confirmText={t('provider:profile.photo.deleteConfirmOk')}
        cancelText={t('provider:profile.photo.deleteConfirmCancel')}
        danger
        loading={deletingPhoto}
        onConfirm={() => void handleConfirmDeletePhoto()}
        onCancel={() => setPhotoDeleteConfirmOpen(false)}
      />
    </div>
  );
}
